import { FC, FormEvent, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import { useTranslation } from '../i18n/useTranslation';

interface OrgSignupPromptCardProps {
  email: string;
  submitting: boolean;
  onSubmit: (organizationName: string) => void;
  onCancel: () => void;
}

const OrgSignupPromptCard: FC<OrgSignupPromptCardProps> = ({
  email,
  submitting,
  onSubmit,
  onCancel,
}) => {
  const t = useTranslation();
  const [orgName, setOrgName] = useState('');

  const trimmed = orgName.trim();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (trimmed && !submitting) onSubmit(trimmed);
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="#f8f8fa"
      p={3}
    >
      <Card
        sx={{
          maxWidth: 480,
          width: '100%',
          p: 5,
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: '12px',
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '16px',
            bgcolor: 'rgba(49,103,155,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <AddBusinessIcon sx={{ fontSize: 28, color: '#31679b' }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, letterSpacing: '-0.02em' }}>
          {t('authGuard.orgSignupPrompt.title')}
        </Typography>
        <Typography sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 3 }}>
          {t('authGuard.orgSignupPrompt.body')}
        </Typography>
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={t('login.email')}
            value={email}
            disabled
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('login.organizationName')}
            placeholder={t('login.organizationNamePlaceholder')}
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            autoFocus
            disabled={submitting}
            sx={{ mb: 3 }}
          />
          <Button
            variant="contained"
            fullWidth
            size="large"
            type="submit"
            disabled={!trimmed || submitting}
            sx={{ mb: 1, textTransform: 'none', py: 1.3 }}
          >
            {submitting ? (
              <CircularProgress size={20} sx={{ color: 'inherit' }} />
            ) : (
              t('authGuard.orgSignupPrompt.submit')
            )}
          </Button>
          <Button
            variant="text"
            fullWidth
            onClick={onCancel}
            disabled={submitting}
            sx={{ textTransform: 'none' }}
          >
            {t('authGuard.orgSignupPrompt.cancel')}
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

export default OrgSignupPromptCard;
